/**
 * Home screen.
 *
 * Two actions:
 *   1. "Create match" → sends `createMatch`. `matchCreated` from server
 *      populates `state.matchId` + `joinUrl`; we then navigate to Match.
 *   2. Paste-a-join-link input → extracts the matchId (full URL or bare)
 *      and navigates to `#/join/<id>`. `JoinScreen` fires the actual
 *      joinMatch when it has a welcome.
 *
 * Connection dot lives in the App shell (top-right).
 */

import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { navigate } from "../router/hashRoute.js";
import { extractMatchId } from "./extractMatchId.js";
import { toModeId, type AbilityId, type ModeId, type QueueSpec } from "@pit/protocol";
import { ChampionPicker } from "../champions/ChampionPicker.js";
import { DEFAULT_CHAMPION_ID } from "../champions/catalog.js";
import { LoadoutPicker, isValidLoadoutSize } from "../champions/LoadoutPicker.js";
import { defaultLoadout } from "../champions/loadout.js";
import { ModePicker } from "./ModePicker.js";
import { MapPicker } from "./MapPicker.js";
import { LIVE_MAP_SLUG } from "../render/arenaCatalog.js";

/** Default quick-match mode: 1v1. */
const DEFAULT_MODE_ID: ModeId = toModeId("1v1");

/**
 * #769: the scroll container.
 *
 * The app shell is viewport-bounded (`#app { height: 100vh }` +
 * `body { overflow: hidden }`, see `index.html`), so anything this screen
 * paints outside its own box is not merely off-screen — it is unreachable:
 * the page itself cannot scroll. `height: 100%` therefore MUST be paired
 * with `overflowY: auto` so tall content becomes scrollable here instead of
 * being clipped away.
 *
 * `justifyContent` must never be `center` on a scroll container: a scroll
 * container cannot scroll above its own start, so centered overflow puts the
 * first children permanently out of reach (that was the original bug — the
 * top spilled AND the bottom "Solo dummy practice"/Tutorial link clipped).
 * Vertical centering-when-it-fits is done by the inner wrapper's auto
 * margins instead (see `HOME_CONTENT_STYLE`).
 */
const HOME_ROOT_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  width: "100%",
  height: "100%",
  overflowY: "auto",
};

/**
 * #769: the content wrapper.
 *
 * `margin: auto` centers the column while there is positive free space and
 * resolves to `0` once free space goes negative (CSS Flexbox § auto margins),
 * i.e. centered on a tall viewport, top-aligned + fully scrollable on a short
 * one. Keeping the padding on THIS element (rather than the scroll container)
 * means the trailing padding stays inside the scrollable area.
 *
 * `clamp()` shrinks the vertical rhythm on short viewports. CSS-only on
 * purpose — no resize listeners, no `window.innerHeight` reads.
 */
const HOME_CONTENT_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  margin: "auto",
  gap: "clamp(10px, 2.2vh, 24px)",
  padding: "clamp(12px, 3vh, 32px)",
};

/**
 * #772: the shared style for the secondary links below the fold.
 *
 * The color comes from the single global `a { color: #7fa5ea }` rule in
 * `index.html` (7.75:1 against the `#0b0f14` page background — WCAG AA needs
 * 4.5:1), NOT from here: these anchors previously set no color at all and
 * rendered in the UA default (#0000EE, #551A8B once visited, ~1.4:1).
 *
 * Deliberately no `opacity`: the old `opacity: 0.75` composites the link
 * toward the background and would drag the contrast ratio back down. The
 * de-emphasis is carried by the smaller font size instead.
 */
const HOME_LINK_STYLE: CSSProperties = { fontSize: 13 };

/**
 * #788: the shared primary-CTA treatment — the solid-green box.
 *
 * Previously the literal values lived inline on the Quick match button (and
 * were near-duplicated on the secondary Create private match button below);
 * #788 needs a SECOND consumer of the CTA treatment (Tutorial), so the shared
 * values live here once instead of being copied again — same consolidation
 * #772 did for `HOME_LINK_STYLE`. Any future home-screen CTA consumes this
 * rather than re-typing the values. Create private match deliberately stays
 * its own smaller, greyer secondary style: it is not a primary CTA.
 *
 * Consumed by BOTH a `<button>` (Quick match) and an `<a>` (Tutorial — kept
 * as an anchor so `#/practice-dummy` stays linkable/middle-clickable), hence
 * `display: inline-block` + `textDecoration: none`, which are no-ops on the
 * button (a flex item's display is blockified anyway).
 *
 * The `#fff`-on-`#3aa675` pairing is inherited verbatim from the Quick match
 * button this extracts — not a new colour decision.
 */
const PRIMARY_CTA_STYLE: CSSProperties = {
  padding: "12px 32px",
  fontSize: 18,
  background: "#3aa675",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  display: "inline-block",
  textDecoration: "none",
};

export function HomeScreen(): ReactElement {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  const [joinInput, setJoinInput] = useState("");
  const [pending, setPending] = useState<"create" | "none">("none");
  // #449: quick-match mode (1v1 vs 3v3), unranked only — see #443 for the
  // not-yet-built ranked queue.
  const [modeId, setModeId] = useState<ModeId>(DEFAULT_MODE_ID);
  const [championId, setChampionId] = useState(DEFAULT_CHAMPION_ID);
  // #323: the chosen ability loadout, defaulted to "everything" whenever the
  // champion changes (every shipped champion's pool is <= MAX_LOADOUT_SIZE
  // today, so this is the whole pool — see `defaultLoadout`).
  const [loadout, setLoadout] = useState<readonly AbilityId[]>(() =>
    defaultLoadout(DEFAULT_CHAMPION_ID),
  );
  // #678: seat-0-only map override for "Create private match" — `quickJoin`
  // carries no `mapSlug` field at all (the wire schema change is scoped to
  // `createMatch` only), so this picker only ever feeds THAT button, never
  // Quick match above it.
  const [mapSlug, setMapSlug] = useState(LIVE_MAP_SLUG);
  const loadoutValid = isValidLoadoutSize(championId, loadout);
  // #499: the practice-mode entry point was originally hidden unless a
  // tester access code was locally saved (`loadTesterAccessCode`) —
  // CONVENIENCE hiding only; the real gate is server-side
  // (`isTesterFlagged()` rejects the request itself, see `ws.ts`'s
  // `createMatch`/`listBots` handling). Pre-release (2026-07-23): every
  // user is shown the entry point unconditionally, matching the server's
  // OWN default-open behavior (`ServerConfig.testerAccessCode` unset ⇒
  // `isTesterFlagged()` already returns `true` for every connection, see
  // `apps/server/src/config.ts`) — there was previously no in-app way to
  // set the local code at all (`saveTesterAccessCode` had zero callers),
  // so this also fixes a real dead-end, not just a policy change. Revert
  // to gated-on-a-saved-code (or wire up a real UI to set one) before
  // public release.
  const practiceModeAvailable = true;

  // When a matchCreated arrives after we clicked Create, hop to Match.
  // Guard on `joinUrl` too: `match.created` sets matchId AND joinUrl in
  // the same reducer pass, so requiring both closes the window where a
  // stale matchId (from an ended match) could satisfy this effect before
  // the fresh create round-trips.
  useEffect(() => {
    if (pending === "create" && state.matchId !== null && state.joinUrl !== null) {
      navigate({ kind: "match" });
      setPending("none");
    }
  }, [pending, state.matchId, state.joinUrl]);

  // If the server rejected our create/join/quickJoin, clear the pending
  // action so the buttons are usable again instead of silently stuck
  // (see #22 — previously `error` was swallowed and `pending` never reset).
  useEffect(() => {
    if (pending === "create" && state.serverError !== null) {
      setPending("none");
    }
  }, [pending, state.serverError]);

  const canConnect = state.connection.kind === "welcomed";

  return (
    <div data-testid="home-root" style={HOME_ROOT_STYLE}>
      <div data-testid="home-content" style={HOME_CONTENT_STYLE}>
        <h1 style={{ margin: 0 }}>PIT</h1>
        <ChampionPicker
          value={championId}
          onChange={(id) => {
            setChampionId(id);
            // #323: a champion switch resets the loadout to that champion's
            // default (today: its whole pool) — a stale loadout from the
            // PREVIOUS champion could otherwise reference abilities that
            // don't even belong to the new one.
            setLoadout(defaultLoadout(id));
          }}
          disabled={!canConnect}
        />
        <LoadoutPicker
          championId={championId}
          value={loadout}
          onChange={setLoadout}
          disabled={!canConnect}
        />
        {/* #788: Tutorial is the loudest control on the page and sits ABOVE
            Quick match — a first-time visitor's path should not be the last
            interactive element below the join-link form (it was, at
            `fontSize: 13`). Placed above the ModePicker + Quick match pair
            rather than between them so the mode select stays adjacent to the
            button it feeds.
            #609: still genuinely ungated — no tester-flag check, client or
            server side (the server's `isTesterFlagged()` carve-out is the
            REAL enforcement; this is simply always shown).
            #772: the visible label is "Tutorial"; the route and the testid
            deliberately keep their original names. */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <div style={{ opacity: 0.75, fontSize: 13 }}>New to Pit?</div>
          <a
            href="#/practice-dummy"
            data-testid="home-dummy-practice-link"
            style={PRIMARY_CTA_STYLE}
          >
            Tutorial
          </a>
        </div>
        <ModePicker value={modeId} onChange={setModeId} disabled={!canConnect} />
        <button
          type="button"
          disabled={!canConnect || !loadoutValid}
          data-testid="home-quick-match"
          onClick={() => {
            // Matchmaking: join an open room in the selected mode, or create
            // one. Same navigate-on-round-trip flow as Create (server replies
            // matchCreated). Ranked stays `false` always — see #443.
            if (state.matchId !== null) dispatch({ type: "match.leave" });
            dispatch({ type: "match.champion", championId, loadout });
            const queue: QueueSpec = { mode: modeId, ranked: false };
            client.quickJoin(queue, championId, [...loadout]);
            setPending("create");
          }}
          style={{
            ...PRIMARY_CTA_STYLE,
            // #788: two adjacent green boxes read as one control (or as a
            // mistake), so Quick match takes the OUTLINED variant of the same
            // CTA geometry while Tutorial above takes the filled one: the
            // loudest treatment goes to what a first-time visitor needs, and
            // a returning player's path is still an unmissable 18px CTA in
            // the same green. `#7fd3ae` on the `#0b0f14` page background is
            // 10.9:1 (WCAG AA needs 4.5:1).
            background: "transparent",
            color: canConnect ? "#7fd3ae" : "#6b7480",
            border: `2px solid ${canConnect ? "#3aa675" : "#333"}`,
          }}
        >
          Quick match
        </button>
        <div style={{ opacity: 0.6, fontSize: 13 }}>— or, for a private match —</div>
        <MapPicker value={mapSlug} onChange={setMapSlug} disabled={!canConnect} />
        <button
          type="button"
          disabled={!canConnect || !loadoutValid}
          data-testid="home-create"
          onClick={() => {
            // Drop any stale identity from a previous (ended) match so the
            // create-effect above only fires on the fresh server round-trip.
            if (state.matchId !== null) dispatch({ type: "match.leave" });
            dispatch({ type: "match.champion", championId, loadout });
            client.createMatch(championId, [...loadout], { mapSlug });
            setPending("create");
          }}
          style={{
            padding: "10px 24px",
            fontSize: 15,
            background: canConnect ? "#2a3340" : "#333",
            color: "#fff",
            border: "1px solid #3a4757",
            borderRadius: 6,
          }}
        >
          Create private match
        </button>
        <div style={{ opacity: 0.75 }}>or paste a join link</div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const id = extractMatchId(joinInput);
            if (id !== null) {
              navigate({ kind: "join", matchId: id });
            }
          }}
          style={{ display: "flex", gap: 8 }}
        >
          <input
            type="text"
            value={joinInput}
            onChange={(e) => {
              setJoinInput(e.target.value);
            }}
            placeholder="matchId or full URL"
            data-testid="home-join-input"
            style={{
              padding: "8px 12px",
              fontSize: 16,
              borderRadius: 4,
              border: "1px solid #444",
              background: "#161c24",
              color: "#dfe6ef",
              minWidth: 280,
            }}
          />
          <button
            type="submit"
            data-testid="home-join-submit"
            disabled={!canConnect || extractMatchId(joinInput) === null}
            style={{
              padding: "8px 16px",
              background: "#5680d6",
              color: "#fff",
              border: "none",
              borderRadius: 4,
            }}
          >
            Join
          </button>
        </form>
        <a href="#/settings" data-testid="home-settings-link" style={HOME_LINK_STYLE}>
          ⚙ Settings
        </a>
        {practiceModeAvailable ? (
          <a href="#/practice" data-testid="home-practice-link" style={HOME_LINK_STYLE}>
            {/* ISSUE-773: the screen behind this link now covers BOTH team
                sizes. Only 3v3 is tester-gated server-side — a solo (1v1)
                practice request is ungated for everyone, same as the Tutorial
                CTA above. */}
            Practice mode (1v1 / 3v3 vs bots)
          </a>
        ) : null}
        {state.connection.kind === "connecting" ? (
          <div style={{ opacity: 0.7 }}>connecting…</div>
        ) : null}
        {import.meta.env.DEV ? (
          <a
            href="#/demo"
            data-testid="home-demo-link"
            style={{ opacity: 0.5, fontSize: 12, marginTop: 24 }}
          >
            dev demo (no server)
          </a>
        ) : null}
      </div>
    </div>
  );
}
